import {
  type Material,
  type MaterialText,
  MATERIAL_MAX_PAGES,
  type NewMaterial,
  type OutlineEntry,
  type PageMime,
  pageImageSize,
  plainText,
  type TextLine,
} from '@shared/material'
import { uploadFile } from '@/board/upload'
import { pdfjs, type PdfDoc, type PdfPage } from './pdf'

// Adding a PDF as a material (MATERIALS.md): the browser derives what the
// server cannot (it has no way to draw a PDF): every page as an image the AI
// can look at, and the text with where each line is. Then it uploads the PDF,
// the derived files, and registers the material.

export type Progress = { phase: 'reading' | 'uploading'; done: number; total: number }

export class MaterialError extends Error {}

const PARALLEL_UPLOADS = 4

/** Where a prepared material goes: a person's session, or a one-time grant (an AI adding one). */
export type MaterialApi = {
  putDerived(name: string, body: Blob): Promise<void>
  register(m: Omit<NewMaterial, 'fileKey'>): Promise<Material>
}

/** A person adding a PDF: uploaded as a board file, then prepared. */
export async function addMaterial(
  boardId: string,
  file: File,
  onProgress: (p: Progress) => void,
): Promise<Material> {
  const bytes = new Uint8Array(await file.arrayBuffer())
  // Read before uploading: a PDF with too many pages is refused before it is stored.
  const doc = await openPdf(bytes)
  try {
    const uploaded = await uploadFile(boardId, file)
    return await prepareMaterial(doc, file.name, sessionApi(boardId, uploaded.fileKey), onProgress)
  } finally {
    void doc.loadingTask.destroy()
  }
}

export async function openPdf(bytes: Uint8Array): Promise<PdfDoc> {
  const m = await pdfjs()
  // pdf.js takes ownership of the buffer it is given: hand it a copy.
  const doc = await m.getDocument({ data: bytes.slice() }).promise
  if (doc.numPages > MATERIAL_MAX_PAGES) {
    void doc.loadingTask.destroy()
    throw new MaterialError('too_many_pages')
  }
  return doc
}

function sessionApi(boardId: string, fileKey: string): MaterialApi {
  return {
    putDerived: async (name, body) => {
      const res = await fetch(`/api/boards/${boardId}/files/${fileKey}/derived/${name}`, {
        method: 'PUT',
        headers: { 'content-type': body.type },
        body,
      })
      if (!res.ok) throw new MaterialError('upload_failed')
    },
    register: async (m) => {
      const res = await fetch(`/api/boards/${boardId}/materials`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...m, fileKey } satisfies NewMaterial),
      })
      if (!res.ok) throw new MaterialError('register_failed')
      return (await res.json()) as Material
    },
  }
}

/**
 * What the server cannot do itself (it has no way to draw a PDF): every page
 * as an image the AI can look at, and the text with where each line is, put
 * beside the stored PDF; then the material is registered.
 */
export async function prepareMaterial(
  doc: PdfDoc,
  name: string,
  api: MaterialApi,
  onProgress: (p: Progress) => void = () => undefined,
): Promise<Material> {
  const pages: NewMaterial['pages'] = []
  const text: MaterialText = { pages: [] }
  let pageMime: PageMime = 'image/webp'
  const uploads = new Set<Promise<void>>()
  let sent = 0
  const total = doc.numPages
  for (let n = 1; n <= total; n++) {
    const page = await doc.getPage(n)
    const base = page.getViewport({ scale: 1 })
    pages.push({ w: base.width, h: base.height })
    text.pages.push(await pageLines(page))
    const image = await pageImage(page)
    if (image.type !== 'image/webp') pageMime = 'image/jpeg'
    onProgress({ phase: 'reading', done: n, total })
    const up = api.putDerived(`pages/${n}`, image).then(() => {
      sent++
    })
    uploads.add(up)
    void up.finally(() => uploads.delete(up))
    if (uploads.size >= PARALLEL_UPLOADS) await Promise.race(uploads)
    page.cleanup()
  }
  onProgress({ phase: 'uploading', done: sent, total })
  await Promise.all(uploads)
  await api.putDerived('text.json', new Blob([JSON.stringify(text)], { type: 'application/json' }))
  onProgress({ phase: 'uploading', done: total, total })
  return api.register({ name, pages, outline: await outline(doc), pageMime })
}

/** The page drawn at the size the AI looks at; WebP where the browser can write it, else JPEG. */
async function pageImage(page: PdfPage): Promise<Blob> {
  const base = page.getViewport({ scale: 1 })
  const size = pageImageSize({ w: base.width, h: base.height })
  const vp = page.getViewport({ scale: size.w / base.width })
  const canvas = document.createElement('canvas')
  canvas.width = size.w
  canvas.height = size.h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new MaterialError('no_canvas')
  ctx.fillStyle = '#fff'
  ctx.fillRect(0, 0, size.w, size.h)
  await page.render({ canvas, viewport: vp }).promise
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/webp', 0.85)
  })
  if (blob?.type === 'image/webp') return blob
  const jpeg = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, 'image/jpeg', 0.88)
  })
  if (!jpeg) throw new MaterialError('no_image')
  return jpeg
}

/** A run of text on one baseline; `xs` is where each of its characters starts (page units). */
type Piece = { str: string; left: number; top: number; right: number; size: number; xs: number[] }

/**
 * The page's text as lines, each with its box as fractions of the page. Text
 * runs are joined into a line while they continue on the same baseline; a
 * wide gap starts a new one (a second column, a table cell).
 */
export async function pageLines(page: PdfPage): Promise<TextLine[]> {
  const m = await pdfjs()
  const vp = page.getViewport({ scale: 1 })
  const content = await page.getTextContent()
  const pieces: Piece[] = []
  for (const it of content.items) {
    if (!('str' in it) || !it.str) continue
    const tx = m.Util.transform(vp.transform, it.transform) as number[]
    const size = Math.hypot(tx[2] ?? 0, tx[3] ?? 0)
    if (!size) continue
    const left = tx[4] ?? 0
    const top = (tx[5] ?? 0) - size
    const right = left + it.width * vp.scale
    // pdf.js gives a run's width, not each glyph's: spread it over the characters.
    const xs = Array.from(
      { length: it.str.length },
      (_, k) => left + ((right - left) * k) / it.str.length,
    )
    pieces.push({ str: it.str, left, top, right, size, xs })
  }
  const lines: Piece[] = []
  let cur: Piece | null = null
  for (const p of pieces) {
    const sameLine =
      cur &&
      Math.abs(p.top - cur.top) < cur.size * 0.5 &&
      p.left > cur.right - cur.size * 0.5 &&
      p.left - cur.right < cur.size * 2.5
    if (cur && sameLine) {
      const gap = p.left - cur.right > cur.size * 0.15 && !/\s$/.test(cur.str) ? ' ' : ''
      if (gap) cur.xs.push(cur.right)
      cur.xs.push(...p.xs)
      cur.str += gap + p.str
      cur.right = Math.max(cur.right, p.right)
      cur.size = Math.max(cur.size, p.size)
    } else {
      if (cur) lines.push(cur)
      cur = { ...p, xs: [...p.xs] }
    }
  }
  if (cur) lines.push(cur)
  const r = (n: number) => Math.round(n * 1e4) / 1e4
  return lines
    .filter((l) => l.str.trim())
    .map((l) => {
      const t = l.str.trim()
      const lead = l.str.length - l.str.trimStart().length
      // One boundary per character, then where the line ends.
      const bounds = [...l.xs, l.right]
      return {
        t: plainText(t),
        x: r(Math.max(0, l.left / vp.width)),
        y: r(Math.max(0, l.top / vp.height)),
        w: r(Math.min(1, (l.right - l.left) / vp.width)),
        h: r(Math.min(1, (l.size * 1.2) / vp.height)),
        xs: bounds.slice(lead, lead + t.length + 1).map((x) => r(Math.min(1, x / vp.width))),
      }
    })
}

type OutlineNode = Awaited<ReturnType<PdfDoc['getOutline']>>[number]

/** The PDF's bookmarks, flattened with their depth; entries that point nowhere are dropped. */
async function outline(doc: PdfDoc): Promise<OutlineEntry[]> {
  const out: OutlineEntry[] = []
  const walk = async (nodes: OutlineNode[], level: number) => {
    for (const n of nodes) {
      if (out.length >= 5000) return
      const page = await destPage(doc, n.dest)
      if (page) out.push({ title: n.title.slice(0, 500), page, level: Math.min(level, 20) })
      await walk(n.items as OutlineNode[], level + 1)
    }
  }
  try {
    await walk((await doc.getOutline()) ?? [], 0)
  } catch {
    // A broken outline is no outline.
  }
  return out
}

/** The page an outline entry or a link inside the PDF goes to, or null. */
export async function destPage(doc: PdfDoc, dest: OutlineNode['dest']): Promise<number | null> {
  const explicit: unknown[] | null =
    typeof dest === 'string' ? await doc.getDestination(dest) : (dest as unknown[] | null)
  const ref: unknown = explicit?.[0]
  if (ref === undefined || ref === null) return null
  if (typeof ref === 'number') return ref + 1
  try {
    return (await doc.getPageIndex(ref as Parameters<PdfDoc['getPageIndex']>[0])) + 1
  } catch {
    return null
  }
}
